import { Box, LinearProgress, Paper } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import SuccessToast from "@/components/SuccessToast";
import { useEditUnitMutation } from "@/mutations/unit.ts";
import { useQueryOptionsFactory } from "@/queries";
import type { ListOwners } from "@/queries/owners.ts";
import type { Unit } from "@/queries/units.ts";
import UnitForm, { type UnitFormData } from "@/routes/admin/-forms/UnitForm.tsx";

export const Route = createFileRoute("/admin/units/$unitId/edit")({
    component: RouteComponent,
    pendingComponent: LinearProgress,
    loader: async ({ context, params }) => {
        await context.queryClient.ensureQueryData(context.qof.owners.list());
        await context.queryClient.ensureQueryData(context.qof.units.get(params.unitId));
    },
});

function RouteComponent() {
    const { unitId } = Route.useParams();
    const navigate = useNavigate();
    const [showSuccessToast, setShowSuccessToast] = useState(false);

    const qof = useQueryOptionsFactory();
    const ownerData: { data: ListOwners[] } = useSuspenseQuery(qof.owners.list()).data;
    const unitData: Unit = useSuspenseQuery(qof.units.get(unitId)).data;

    const editUnitMutation = useEditUnitMutation(unitId);

    const handleEditUnit = (data: UnitFormData) => {
        editUnitMutation.mutate({ attributes: data, type: "calendar" });
        setShowSuccessToast(true);
    };
    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <PageHeader
                    title={`Edit Unit Data - Unit #${unitData.rentalId} (${unitData.owner.ownerName})`}
                    instructions={[
                        `Edit unit data for Unit #${unitData.rentalId} (${unitData.owner.ownerName})`,
                    ]}
                    onBack={() => navigate({ to: "/admin/units" })}
                    backText="← Back to All Units"
                />
                <UnitForm unit={unitData} owners={ownerData} submitHandler={handleEditUnit} />
            </Paper>
            <SuccessToast
                open={showSuccessToast}
                onClose={() => setShowSuccessToast(false)}
                title="Unit Data Updated Successfully!"
                message="The unit information has been saved to the database."
            />
        </Box>
    );
}
